<template>
  <page-wrapper v-if="profile?.profile" :breadcrumbs="breadcrumbs" transparent-bg>
    <template #title>
      <div class="h3">{{ userFullName }}</div>
    </template>

    <page-block>
      <div class="d-flex">
        <property-block class="flex-grow-1" content-max-width="800px">
          <template #text>
            <div class="d-flex align-center flex-column">
              <avatar
                :avatar="profile.profile.userAvatar"
                :literal="profile.profile.name.slice(0, 2)"
                show-error
                size="XXL"
              />
              <div class="d-flex align-center mt-5">
                <user-avatar-dialog />
                <template v-if="profile.profile.userAvatar?.url">
                  <vertical-line class="mr-2" />
                  <e-btn :loading="isRemoving" class="text-red" icon variant="link" @click="removeAvatar">
                    <e-icon name="trash" />
                  </e-btn>
                </template>
              </div>
            </div>
          </template>
          <template #content>
            <div :class="{ 'mt-0': isEditingUserProfile, 'mt-6': !isEditingUserProfile }">
              <main-user-info v-if="!isEditingUserProfile" :user="profile" />
              <e-form v-else v-model="isValid" class="w-100" @submit="submit">
                <e-input
                  v-model="editingUserInfo.lastName"
                  :rules="[required()]"
                  ref="lastNameInputRef"
                  label="Фамилия"
                  class="mb-4"
                />
                <e-input v-model="editingUserInfo.name" :rules="[required()]" label="Имя" class="mb-4" />
                <e-input v-model="editingUserInfo.middleName" label="Отчество" class="mb-4" />
                <phone-field v-model="editingUserInfo.phoneNumber" class="mb-4" :rules="[required()]" />
                <e-input v-model="editingUserInfo.email" :rules="[required()]" disabled label="Email" />
              </e-form>
            </div>
          </template>
          <template #right>
            <div class="ml-auto user__buttons">
              <e-btn v-if="!isEditingUserProfile" variant="primary" @click="editProfile">
                <template #prepend>
                  <e-icon name="editPen" />
                </template>
                Редактировать
              </e-btn>

              <template v-else>
                <e-btn :disabled="!isValid" variant="primary" :loading="isLoading" @click="submit"> Сохранить</e-btn>

                <e-btn variant="secondary" @click="cancel"> Отмена</e-btn>
              </template>
            </div>
          </template>
        </property-block>
      </div>
    </page-block>

    <page-block class="mt-4" full-page>
      <property-block>
        <template #text>
          <div class="h4 text-secondary mb-6">Доступы</div>
        </template>
        <template #content>
          <e-select
            :model-value="profile?.isOwner ? USER_ROLE_MAP.OWNER : profile!.role!"
            :items="USER_ROLE_SELECT_ITEMS"
            label="Роль в пространстве"
            disabled
          />
        </template>
      </property-block>
    </page-block>
  </page-wrapper>
</template>

<script setup lang="ts">
import { nextTick, ref, toRef } from 'vue';
import type { BreadcrumbsItem } from '@/core/types/components/helps';
import type { IInput } from '@/core/types/components/input';
import Avatar from '@/core/components/common/Avatar.vue';
import PhoneField from '@/core/components/fields/PhoneField.vue';
import VerticalLine from '@/core/components/helpers/VerticalLine.vue';
import PageBlock from '@/core/components/interface/PageBlock.vue';
import PageWrapper from '@/core/components/interface/PageWrapper.vue';
import PropertyBlock from '@/core/components/interface/PropertyBlock.vue';
import { EBtn, EForm, EIcon, EInput, ESelect } from '@/core/components/ui';
import { useServices } from '@/core/composables/services';
import { RouteNames } from '@/core/plugins/router/routeNames';
import { clearPhoneFormat } from '@/core/utils/string';
import { required } from '@/core/utils/validation';
import { RemoveUserAvatar } from '@/parts/auth/gql/user';
import { useAuthStore } from '@/parts/auth/store/auth';
import MainUserInfo from '@/parts/users/components/MainUserInfo.vue';
import UserAvatarDialog from '@/parts/users/components/UserAvatarDialog.vue';
import { USER_ROLE_MAP, USER_ROLE_SELECT_ITEMS } from '@/parts/users/utils/consts';

const { $api } = useServices();
const { updateProfile, getProfile, profile } = useAuthStore();

const editingUserInfo = ref({
  lastName: '',
  name: '',
  middleName: '' as string | null | undefined,
  phoneNumber: '',
  email: '',
});

const lastNameInputRef = ref<IInput>();

const isEditingUserProfile = ref(false);
const isValid = ref(false);

const editProfile = async () => {
  if (!profile.value?.profile) {
    return;
  }

  isEditingUserProfile.value = true;

  editingUserInfo.value = {
    lastName: profile.value.profile.lastName,
    name: profile.value.profile.name,
    middleName: profile.value.profile.middleName,
    phoneNumber: profile.value.profile.phoneNumber,
    email: profile.value.email,
  };

  await nextTick();

  lastNameInputRef.value?.focus();
};

const cancel = () => {
  isEditingUserProfile.value = false;
};

const isLoading = ref(false);

const submit = async () => {
  if (!isValid.value) {
    return;
  }

  isLoading.value = true;

  const result = await updateProfile({
    phoneNumber: clearPhoneFormat(editingUserInfo.value.phoneNumber),
    name: editingUserInfo.value.name,
    lastName: editingUserInfo.value.lastName,
    middleName: editingUserInfo.value.middleName,
  });

  if (!result?.errors) {
    await getProfile(true);

    cancel();
  }

  isLoading.value = false;
};

const isRemoving = ref(false);

const removeAvatar = async () => {
  isRemoving.value = true;

  const result = await $api.mutation(RemoveUserAvatar);

  if (!result?.errors) {
    await getProfile(true);
  }

  isRemoving.value = false;
};

const userFullName = toRef(() =>
  profile.value
    ? `${profile.value.profile?.lastName} ${profile.value.profile?.name} ${profile.value.profile?.middleName ? profile.value.profile?.middleName : ''}`
    : '',
);

const breadcrumbs = toRef<() => BreadcrumbsItem[]>(() => [
  {
    icon: 'users3',
    title: 'Пользователи',
    to: {
      name: RouteNames.Users,
    },
  },
  {
    title: userFullName.value,
  },
]);
</script>

<style scoped lang="scss">
.user {
  &__buttons {
    display: flex;
    flex-direction: column;

    gap: 16px;

    @include md-and-down {
      flex-direction: row-reverse;
    }

    @include xs {
      width: 100%;
    }

    @include extra-xs {
      gap: 8px;

      :deep(button) {
        width: calc(50% - 4px);
      }
    }
  }
}
</style>
